import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, User, Mail, ArrowRight, UserPlus, Loader2, Server, Wifi, WifiOff } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const Login = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [serverStatus, setServerStatus] = useState("checking"); // checking, online, offline, waking
  const serverStatusRef = useRef(serverStatus);
  useEffect(() => {
    serverStatusRef.current = serverStatus;
  }, [serverStatus]);

  // Check server connectivity on mount
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    const checkServer = async () => {
      setServerStatus("checking");
      
      try {
        const response = await axios.get(`${API}/`, { 
          timeout: 10000,
          signal: controller.signal
        });
        if (isMounted && response.data) {
          setServerStatus("online");
        }
      } catch (error) {
        if (!isMounted) return;
        
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return; // Request was cancelled, ignore
        }
        
        console.error("Server check error:", error);
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          setServerStatus("waking");
          try {
            await axios.get(`${API}/`, { timeout: 30000 });
            if (isMounted) {
              setServerStatus("online");
              toast.success("Server is ready!");
            }
          } catch {
            if (isMounted) setServerStatus("offline");
          }
        } else if (error.response) {
          setServerStatus("online");
        } else {
          setServerStatus("offline");
        }
      }
    };
    
    checkServer();
    
    // Keep-alive ping every 4 minutes
    const keepAliveInterval = setInterval(() => {
      if (serverStatusRef.current === "online") {
        axios.get(`${API}/`).catch(() => {});
      }
    }, 240000);
    
    return () => {
      isMounted = false;
      controller.abort();
      clearInterval(keepAliveInterval);
    };
  }, []);

  const checkServerStatus = async () => {
    setServerStatus("checking");
    
    try {
      const response = await axios.get(`${API}/`, { timeout: 10000 });
      if (response.data) {
        setServerStatus("online");
      }
    } catch (error) {
      console.error("Server check error:", error);
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        setServerStatus("waking");
        // Retry with longer timeout
        try {
          await axios.get(`${API}/`, { timeout: 30000 });
          setServerStatus("online");
          toast.success("Server is ready!");
        } catch {
          setServerStatus("offline");
        }
      } else if (error.response) {
        // Server responded - it's online
        setServerStatus("online");
      } else {
        setServerStatus("offline");
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!credentials.username || !credentials.password) {
      toast.error(isRegister ? "Please enter your work email and a password" : "Please enter your email (or username) and password");
      return;
    }
    if (isRegister && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(credentials.username.trim())) {
      toast.error("Please enter a valid work email");
      return;
    }

    setLoading(true);

    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const response = await axios.post(`${API}${endpoint}`, isRegister
        ? { email: credentials.username.trim().toLowerCase(), password: credentials.password }
        : { username: credentials.username.trim(), password: credentials.password });

      const data = response.data;
      
      if (data.success) {
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("username", data.username);
        localStorage.setItem("userId", data.userId);
        if (data.token) localStorage.setItem("authToken", data.token);
        if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
        
        // Store onboarding status for the dashboard to use immediately
        if (data.onboardingComplete) {
          localStorage.setItem(`onboardingComplete_${data.userId}`, "true");
        }
        
        toast.success(isRegister ? "Account created!" : "Login successful!");
        
        // Navigate based on onboarding status
        if (data.onboardingComplete) {
          navigate(`/dashboard/${data.userId}`);
        } else {
          navigate("/dashboard/new");
        }
      } else {
        toast.error(data.message || "Authentication failed");
      }
    } catch (error) {
      console.error("Auth error:", error);
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          error.response?.data?.message ||
          "Authentication failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center relative overflow-hidden">
      {/* Cyber Grid Background */}
      <div className="absolute inset-0 cyber-grid opacity-20" />

      {/* Login Card */}
      <motion.div 
        className="relative z-10 w-full max-w-md mx-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="glass-card holo-border p-8 relative overflow-hidden">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_pro-advisors-4/artifacts/00joaz6p_017767_Logo%20FInal%20File-01.png" 
                alt="Intelligent Sales Machines" 
                className="h-32 w-auto"
              />
            </div>
            <p className="text-gray-500 text-sm">Enterprise Sales Intelligence Platform</p>
          </div>

          {/* Server Status Indicator */}
          <AnimatePresence mode="wait">
            {serverStatus !== "online" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className={`rounded-xl p-4 border ${
                  serverStatus === "checking" ? "bg-gray-500/10 border-gray-500/30" :
                  serverStatus === "waking" ? "bg-amber-500/10 border-amber-500/30" :
                  "bg-red-500/10 border-red-500/30"
                }`}>
                  <div className="flex items-center gap-3">
                    {serverStatus === "checking" && (
                      <>
                        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                        <div>
                          <p className="text-sm text-gray-300">Connecting to server...</p>
                          <p className="text-xs text-gray-500">Please wait</p>
                        </div>
                      </>
                    )}
                    {serverStatus === "waking" && (
                      <>
                        <Server className="h-5 w-5 text-amber-400 animate-pulse" />
                        <div>
                          <p className="text-sm text-amber-300">Server is waking up...</p>
                          <p className="text-xs text-amber-400/70">This may take 10-30 seconds</p>
                        </div>
                      </>
                    )}
                    {serverStatus === "offline" && (
                      <>
                        <WifiOff className="h-5 w-5 text-red-400" />
                        <div className="flex-1">
                          <p className="text-sm text-red-300">Server unavailable</p>
                          <p className="text-xs text-red-400/70">Please try again later</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={checkServerStatus}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30"
                        >
                          Retry
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                {isRegister ? "Work email" : "Email or username"}
              </label>
              <div className="relative">
                {isRegister ? <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" /> : <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />}
                <Input
                  type={isRegister ? "email" : "text"}
                  autoComplete={isRegister ? "email" : "username"}
                  value={credentials.username}
                  onChange={(e) =>
                    setCredentials({ ...credentials, username: e.target.value })
                  }
                  className="w-full bg-white/5 border-white/10 text-white pl-10 rounded-xl focus:border-ism-teal transition-all placeholder:text-gray-600"
                  placeholder={isRegister ? "you@yourcompany.com" : "you@yourcompany.com or username"}
                  disabled={loading || serverStatus !== "online"}
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
                <Input
                  type="password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials({ ...credentials, password: e.target.value })
                  }
                  className="w-full bg-white/5 border-white/10 text-white pl-10 rounded-xl focus:border-ism-teal transition-all placeholder:text-gray-600"
                  placeholder="Enter your password"
                  disabled={loading || serverStatus !== "online"}
                  data-testid="login-password"
                />
              </div>
            </div>

            <div>
              <Button
                type="submit"
                disabled={loading || serverStatus !== "online"}
                className="w-full bg-ism-teal hover:bg-ism-teal/90 text-black font-bold py-3 rounded-xl transition-all border-0 text-base disabled:opacity-50"
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full mr-2 animate-spin" />
                    {isRegister ? "Creating Account..." : "Signing In..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isRegister ? (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Create Account
                      </>
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Toggle Register/Login */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-ism-teal hover:text-ism-teal/80 transition-colors"
              data-testid="toggle-register"
            >
              {isRegister ? "Already have an account? Sign In" : "Need an account? Register"}
            </button>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-6 text-xs text-gray-600">
          © Copyright 2026 Intelligent Sales Machines, Inc. – All rights reserved.
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
